import React, { useState, useCallback } from 'react';

export type VisualSelectionMode = 'all' | 'firstN' | null;

export interface RowSelectionState {
  rowSelectionMode: boolean;
  selectedRowIds: Set<string>;
  visualSelectionMode: VisualSelectionMode;
  visualSelectionCount: number;
}

export interface UseRowSelectionReturn extends RowSelectionState {
  setSelectedRowIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  enterRowSelectionMode: () => void;
  exitRowSelectionMode: () => void;
  selectAllRows: () => void;
  selectFirstNRows: (n: number) => void;
  clearSelection: () => void;
}

export const useRowSelection = (): UseRowSelectionReturn => {
  const [rowSelectionMode, setRowSelectionMode] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [visualSelectionMode, setVisualSelectionMode] = useState<VisualSelectionMode>(null);
  const [visualSelectionCount, setVisualSelectionCount] = useState(0);

  const enterRowSelectionMode = useCallback(() => {
    setRowSelectionMode(true);
    setSelectedRowIds(new Set());
    setVisualSelectionMode(null);
  }, []);

  const exitRowSelectionMode = useCallback(() => {
    setRowSelectionMode(false);
    setVisualSelectionMode(null);
  }, []);

  const selectAllRows = useCallback(() => {
    setVisualSelectionMode('all');
    setRowSelectionMode(false);
  }, []);

  const selectFirstNRows = useCallback((n: number) => {
    setVisualSelectionMode('firstN');
    setVisualSelectionCount(n);
    setRowSelectionMode(false);
  }, []);

  const clearSelection = useCallback(() => {
    setRowSelectionMode(false);
    setSelectedRowIds(new Set());
    setVisualSelectionMode(null);
    setVisualSelectionCount(0);
  }, []);

  return {
    rowSelectionMode,
    selectedRowIds,
    visualSelectionMode,
    visualSelectionCount,
    setSelectedRowIds,
    enterRowSelectionMode,
    exitRowSelectionMode,
    selectAllRows,
    selectFirstNRows,
    clearSelection,
  };
};
